import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  ViewChild,
} from "@angular/core";
import { formatMessage } from "devextreme/localization";
import { CalculatorUsFieldItemsService } from "../../../../../services/calculator-us-field-items.service";
import { UsCalculatorFieldItem } from "../../../../../model/calculator-us-remastered/usCalculatorFieldItem";
import { DxAccordionComponent, DxFormComponent } from "devextreme-angular";
import { PaperSpecification } from "../../../../../model/calculator-us-remastered/paperSpecification";
import { SummaryItem } from "../../../../../model/calculator-us-remastered/summaryItem";
import { SelectedOption } from "../option-mapping-select-box/option-mapping-select-box.component";

@Component({
  selector: "app-calculator-paper-specification",
  templateUrl: "./calculator-paper-specification.component.html",
  styleUrls: ["./calculator-paper-specification.component.scss"],
})
export class CalculatorPaperSpecificationComponent implements OnChanges {
  @Input() selectedPublisherId: string | undefined;
  @Input() selectedClassification: string | undefined;
  @Input() printedEndsheetActive: boolean = false; // Wenn Component Specification Endsheet aktiv ist
  @Output() summaryUpdate = new EventEmitter<SummaryItem[]>();
  @Output() onValueChanged = new EventEmitter<PaperSpecification>();

  @ViewChild("paperForm") paperForm: DxFormComponent;
  @ViewChild("accordion") accordion: DxAccordionComponent;

  formatMessage = formatMessage;
  public collapsed: any = true;

  private _paperSpecification: PaperSpecification;
  @Input()
  set paperSpecification(val: PaperSpecification) {
    this._paperSpecification = val;
  }
  get paperSpecification(): PaperSpecification {
    return this._paperSpecification;
  }

  // Field items for dropdowns
  paperTypeFieldItems: UsCalculatorFieldItem[] = [];
  paperBleedsFieldItems: UsCalculatorFieldItem[] = []; // Renamed from paperWeightFieldItems
  bookPaperColorFieldItems: UsCalculatorFieldItem[] = [];

  _selectedPaperType: UsCalculatorFieldItem | null = null;
  get selectedPaperType() {
    return this._selectedPaperType;
  }
  set selectedPaperType(val: UsCalculatorFieldItem | null) {
    this._selectedPaperType = val;
    this.paperSpecification.paperType = val?.keyValue || undefined;
    this.onValueChanged.emit(this.paperSpecification);
  }

  _selectedPaperBleeds: UsCalculatorFieldItem | null = null;
  get selectedPaperBleeds() {
    return this._selectedPaperBleeds;
  }
  set selectedPaperBleeds(val: UsCalculatorFieldItem | null) {
    this._selectedPaperBleeds = val;
    this.paperSpecification.paperBleeds = val?.keyValue || undefined;
    this.onValueChanged.emit(this.paperSpecification);
  }

  _selectedEndsheet: SelectedOption | undefined = undefined;
  get selectedEndsheet(): SelectedOption | undefined {
    return this._selectedEndsheet;
  }
  set selectedEndsheet(val: SelectedOption | undefined) {
    this._selectedEndsheet = val;
    this.paperSpecification.endSheetOptionId = val?.optionId || undefined;
    this.onValueChanged.emit(this.paperSpecification);
  }

  _selectedBookPaperColor: UsCalculatorFieldItem | null = null;
  get selectedBookPaperColor() {
    return this._selectedBookPaperColor;
  }
  set selectedBookPaperColor(val: UsCalculatorFieldItem | null) {
    this._selectedBookPaperColor = val;
    this.paperSpecification.bookPaperColor = val?.keyValue || undefined;
    this.onValueChanged.emit(this.paperSpecification);
  }

  endsheetSummaryItems: SummaryItem[] = [];

  onAccordionClick(e) {
    this.collapsed = e.component.option("selectedIndex") == -1;
  }

  constructor(
    private calculatorUsFieldItemsService: CalculatorUsFieldItemsService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedClassification) {
      this.resetFieldItems();
      this.loadFieldItems();
    }

    if (changes.printedEndsheetActive) {
      this.updateSummary();
    }
  }

  private loadFieldItems(): void {
    if (!this.selectedPublisherId || !this.selectedClassification) {
      return;
    }

    // Verwende optimierte Methode für gesammelte Subkategorie-Abfrage
    this.calculatorUsFieldItemsService
      .getFieldItemsBySubcategory(
        this.selectedPublisherId,
        this.calculatorUsFieldItemsService.getDbClassification(
          this.selectedClassification
        ),
        "BOOK_PAPER"
      )
      .subscribe((fieldItems) => {
        // Reset arrays
        this.paperTypeFieldItems = [];
        this.paperBleedsFieldItems = [];
        this.bookPaperColorFieldItems = [];

        // Mapping über fieldItem.inputName wie in der Hauptkomponente
        for (const fieldItem of fieldItems) {
          if (fieldItem.inputName === "PAPER_TYPE") {
            this.paperTypeFieldItems.push(fieldItem);
            if (fieldItem.isDefault) {
              this.selectedPaperType = fieldItem;
            }
          } else if (fieldItem.inputName === "BLEEDS") {
            this.paperBleedsFieldItems.push(fieldItem);
            // Set default to "no bleeds" for bleeds, or service default as fallback
            if (fieldItem.isDefault) {
              this.selectedPaperBleeds = fieldItem;
            }
          } else if (fieldItem.inputName === "PAPER_COLOR") {
            this.bookPaperColorFieldItems.push(fieldItem);
            if (fieldItem.isDefault) {
              this.selectedBookPaperColor = fieldItem;
            }
          }
        }

        // Update summary after all data is loaded
        this.updateSummary();
      });
  }

  setEndsheetSummaryItems($event: any): void {
    const summaryItems: SummaryItem[] = $event;
    this.endsheetSummaryItems = [...summaryItems];
    this.updateSummary();
  }

  /**
   * Aktualisiert die Summary mit den aktuellen Paper Specification Werten
   */
  private updateSummary(): void {
    const summaryItems: SummaryItem[] = [...this.endsheetSummaryItems];

    // Paper Type
    if (this.paperTypeFieldItems.length > 0) {
      const defaultItem =
        this.paperTypeFieldItems.find((item) => item.isDefault) || null;
      summaryItems.push(
        new SummaryItem(
          "bookPaper",
          "Paper Type",
          this.selectedPaperType?.displayValue || "",
          defaultItem?.displayValue || "",
          "Paper Specification",
          undefined
        )
      );
    }

    // Paper Bleeds (renamed from Paper Weight)
    if (this.paperBleedsFieldItems.length > 0) {
      // Use custom default "no bleeds" instead of service default
      const customDefault = this.paperBleedsFieldItems.find(
        (item) =>
          item.displayValue?.toLowerCase().includes("no bleeds") ||
          item.displayValue?.toLowerCase().includes("no bleed")
      );
      const fallbackDefault =
        this.paperBleedsFieldItems.find((item) => item.isDefault) || null;
      const defaultItem = customDefault || fallbackDefault;

      summaryItems.push(
        new SummaryItem(
          "bookPaperBleeds",
          "Bleeds",
          this.selectedPaperBleeds?.displayValue || "",
          defaultItem?.displayValue || "",
          "Paper Specification",
          undefined
        )
      );
    }

    // Book Paper Color
    if (this.bookPaperColorFieldItems.length > 0) {
      const defaultItem =
        this.bookPaperColorFieldItems.find((item) => item.isDefault) || null;
      summaryItems.push(
        new SummaryItem(
          "bookPaperColor",
          "Book Paper Color",
          this.selectedBookPaperColor?.displayValue || "",
          defaultItem?.displayValue || "",
          "Paper Specification",
          undefined
        )
      );
    }

    // Emit summary update
    this.summaryUpdate.emit(summaryItems);
  }

  // Event handlers
  paperTypeChanged($event: any) {
    const selectedValue = $event.value;
    this.selectedPaperType =
      this.paperTypeFieldItems.find(
        (item) => item.keyValue === selectedValue
      ) || null;
    this.updateSummary();
  }

  paperBleedsChanged($event: any) {
    const selectedValue = $event.value;
    this.selectedPaperBleeds =
      this.paperBleedsFieldItems.find(
        (item) => item.keyValue === selectedValue
      ) || null;

    this.updateSummary();
  }

  paperEndsheetChanged(value: SelectedOption | undefined) {
    this.selectedEndsheet = value
  }

  bookPaperColorChanged($event: any) {
    const selectedValue = $event.value;
    this.selectedBookPaperColor =
      this.bookPaperColorFieldItems.find(
        (item) => item.keyValue === selectedValue
      ) || null;

    this.updateSummary();
  }

  public resetFieldItems(): void {
    this.endsheetSummaryItems = [];

    this.paperTypeFieldItems = [];
    this.paperBleedsFieldItems = [];
    this.bookPaperColorFieldItems = [];

    this.selectedPaperType = null;
    this.selectedPaperBleeds = null;
    this.selectedEndsheet = undefined;
    this.selectedBookPaperColor = null;

    if (this.accordion) this.accordion.instance.collapseItem(0).then();
    this.summaryUpdate.emit([]);
  }
}
